/**
 * Image processing utilities for Station Media
 * Handles cropping, resizing, and compression
 */

import imageCompression from 'browser-image-compression';
import { Area } from 'react-easy-crop';
import { StationImageType } from '../types';

/**
 * Output configuration for each image type
 */
export const IMAGE_OUTPUT_CONFIGS = {
  icon: {
    width: 512,
    height: 512,
    maxSizeMB: 2,
    aspectRatio: 1,
  },
  banner: {
    width: 1200,
    height: 400,
    maxSizeMB: 5,
    aspectRatio: 3,
  },
  gallery: {
    maxSizeMB: 5,
    maxWidthOrHeight: 1920,
    aspectRatio: undefined, // Free aspect ratio
  },
} as const;

/**
 * Creates an image element from a source URL
 */
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Converts canvas to a Blob
 */
const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas to blob conversion failed'));
        }
      },
      mimeType,
      0.92 // Quality for JPEG
    );
  });

/**
 * Applies crop to an image and returns a new image at the specified dimensions
 *
 * @param imageSrc - Source URL of the image (can be object URL or data URL)
 * @param pixelCrop - The crop area in pixels from react-easy-crop
 * @param outputWidth - Target output width
 * @param outputHeight - Target output height
 * @param mimeType - Output mime type (defaults to image/jpeg)
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth: number,
  outputHeight: number,
  mimeType: string = 'image/jpeg'
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Unable to get canvas context');
  }

  // Set canvas dimensions to the desired output size
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  // Draw the cropped image onto the canvas, scaling to fit output dimensions
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputWidth,
    outputHeight
  );

  return canvasToBlob(canvas, mimeType);
}

/**
 * Applies crop to an image preserving the cropped dimensions
 * Used for gallery images where we don't want to force specific dimensions
 *
 * @param imageSrc - Source URL of the image
 * @param pixelCrop - The crop area in pixels from react-easy-crop
 * @param mimeType - Output mime type
 */
export async function getCroppedImgPreserveDimensions(
  imageSrc: string,
  pixelCrop: Area,
  mimeType: string = 'image/jpeg'
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Unable to get canvas context');
  }

  // Use the crop dimensions as output dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvasToBlob(canvas, mimeType);
}

/**
 * Compresses an image file using browser-image-compression
 *
 * @param file - The file or blob to compress
 * @param maxSizeMB - Maximum file size in MB
 * @param maxWidthOrHeight - Maximum width or height (optional)
 */
export async function compressImage(
  file: File | Blob,
  maxSizeMB: number,
  maxWidthOrHeight?: number
): Promise<File> {
  // Convert Blob to File if necessary
  const inputFile =
    file instanceof File ? file : new File([file], 'image.jpg', { type: file.type });

  const options: Parameters<typeof imageCompression>[1] = {
    maxSizeMB,
    maxWidthOrHeight,
    useWebWorker: true,
    fileType: 'image/jpeg',
  };

  const compressedFile = await imageCompression(inputFile, options);
  return compressedFile;
}

/**
 * Process an image for upload - crop (if provided) and compress
 *
 * @param imageSrc - Source URL of the image
 * @param imageType - Type of station image (icon, banner, gallery)
 * @param pixelCrop - Optional crop area (if user performed cropping)
 * @param originalFile - Original file for metadata
 */
export async function processImageForUpload(
  imageSrc: string,
  imageType: StationImageType,
  pixelCrop: Area | null,
  originalFile: File
): Promise<File> {
  const config = IMAGE_OUTPUT_CONFIGS[imageType];
  let processedBlob: Blob;

  if (pixelCrop) {
    // Apply crop
    if (imageType === 'gallery') {
      // Gallery: preserve dimensions from crop
      processedBlob = await getCroppedImgPreserveDimensions(imageSrc, pixelCrop);
    } else {
      // Icon/Banner: force specific output dimensions
      const { width, height } = config as { width: number; height: number };
      processedBlob = await getCroppedImg(imageSrc, pixelCrop, width, height);
    }
  } else {
    // No crop - use original file
    processedBlob = originalFile;
  }

  // Compress the image
  const maxWidthOrHeight = imageType === 'gallery'
    ? IMAGE_OUTPUT_CONFIGS.gallery.maxWidthOrHeight
    : undefined;
  const compressedFile = await compressImage(
    processedBlob,
    config.maxSizeMB,
    maxWidthOrHeight
  );

  // Create a new file with the original filename
  const extension = 'jpg';
  const baseName = originalFile.name.replace(/\.[^/.]+$/, '');
  const newFileName = `${baseName}.${extension}`;

  return new File([compressedFile], newFileName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Compress an image without cropping (for gallery skip flow)
 *
 * @param file - The original file
 */
export async function compressImageOnly(file: File): Promise<File> {
  const config = IMAGE_OUTPUT_CONFIGS.gallery;

  const compressedFile = await compressImage(file, config.maxSizeMB, config.maxWidthOrHeight);

  return new File([compressedFile], file.name, {
    type: compressedFile.type,
    lastModified: Date.now(),
  });
}

/**
 * Get the aspect ratio for a given image type
 */
export function getAspectRatioForType(imageType: StationImageType): number | undefined {
  return IMAGE_OUTPUT_CONFIGS[imageType].aspectRatio;
}

/**
 * Get output dimensions label for display
 */
export function getOutputDimensionsLabel(imageType: StationImageType): string {
  const config = IMAGE_OUTPUT_CONFIGS[imageType];
  if ('width' in config && 'height' in config) {
    return `${config.width} x ${config.height} px`;
  }
  return 'Original dimensions (compressed)';
}
